import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, take, tap } from 'rxjs/operators';
import { Observable, BehaviorSubject, of, from } from 'rxjs';
import { Settings } from 'src/app/shared/interface';
import { environment } from 'src/environments/environment.prod';
import { CacherService } from '../cache/cacher.service';
import { Network } from '@capacitor/network';
import { FunctionsService } from '../functions/functions.service';
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private navParams: any = {};
  private refreshBehaviorSubject: BehaviorSubject<string> = new BehaviorSubject('');
  private userBehaviorSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public isOffline = false;

  isAdmin: boolean = true;
  privatePrice: boolean = false;

  settings: Settings = {
    otp: false,
    balance: false,
    showAvailable: false,
    country: false,
    note: false,
    pendingUser: false,
    loginRequired: false,
    currency: 'د.ع',
    order: true,
    page: '/home',
    searchPage: false,
    bestSeller: false,
    special: false,
  };

  private baseURL: string = environment.baseURL;

  constructor(
    private http: HttpClient,
    private cacher: CacherService<string>,
    private helpers:FunctionsService,
    private plt:Platform
  ) {
    this.monitorNetworkStatus();
  }

  // Monitor network status to detect offline state
  private async monitorNetworkStatus() {
    const status = await Network.getStatus();
    if (!status.connected){
      this.helpers.presentToast('انت غير متصل بالانترنت', true);
    }
    this.isOffline = !status.connected;

    Network.addListener('networkStatusChange', (status) => {
      this.isOffline = !status.connected;
    });

    if (!this.isOffline) {
      this.replayCachedRequests();
    }
  }

  set params(body: any) {
    this.navParams = body;
  }

  get params() {
    return this.navParams;
  }

  get user(): any {
    return this.userBehaviorSubject.value;
  }

  set user(value: any) {
    this.userBehaviorSubject.next(value || null);
  }

  get user$(): Observable<any> {
    return this.userBehaviorSubject.asObservable();
  }

  public getNetworkStatus() {
    return this.isOffline;
  }

  private fetchApi<T>(observable: Observable<T>): Observable<T> {
    return observable.pipe(
      take(1),
      catchError((error) => {
        console.error('API error:', error);
        return of(null); // Gracefully handle errors
      })
    );
  }


  getData<T>(endPoint: string): Observable<T | null> {
    const key = `${endPoint}`;

  // If offline, fetch data from cache
    if (this.isOffline) {
      return from(this.fetchCachedData(key)).pipe(
        catchError((error) => {
          console.error('API error:', error);
          return of(null); // Gracefully handle errors
        })
      );
    }
  // Online: Fetch data from API
    return this.http.get<T>(`${this.baseURL}${endPoint}`).pipe(
    take(1), // Automatically complete the observable after receiving one value
      tap((data:any) => {
        if (data) {
          this.cacher.set(key, data); // Cache the data
        }
      }),
      catchError((error) => {
        console.error('API error:', error);
        return of(null); // Gracefully handle errors
      })
    );
}

  // Helper function to fetch cached data
  private async fetchCachedData(key: string): Promise<any | null> {
    try {
      const cachedData = await this.cacher.get(key);
      return cachedData;
    } catch (error) {
      console.error('Error fetching cached data:', error);
      return null;
    }
  }

  postData<T>(endPoint: string, body: any): Observable<T> {
    if (this.isOffline) {
      console.warn('App is offline. Cannot send data.');
      return of(null as T); // Return null when offline
    }
    return this.http.post<T>(`${this.baseURL}${endPoint}`, body).pipe(take(1));
  }

  updateData<T>(endPoint: string, body: any): Observable<T> {
    if (this.isOffline) {
      this.helpers.presentToast('انت غير متصل بالانترنت', true);
      console.warn('App is offline. Cannot update data.');
      return of(null as T); // Return null when offline
    }
    return this.http.put<T>(`${this.baseURL}${endPoint}`, body).pipe(take(1));
  }

  deleteData<T>(endPoint: string): Observable<T> {
    if (this.isOffline) {
      this.helpers.presentToast('انت غير متصل بالانترنت', true);
      console.warn('App is offline. Cannot delete data.');
      return of(null as T); // Return null when offline
    }
    return this.http.delete<T>(`${this.baseURL}${endPoint}`).pipe(take(1));
  }



  private cacheRequest(method: string, endPoint: string, body: any): void {
    const cachedRequests = JSON.parse(localStorage.getItem('cachedRequests') || '[]');
    cachedRequests.push({ method, endPoint, body, timestamp: Date.now() });
    localStorage.setItem('cachedRequests', JSON.stringify(cachedRequests));
  }


  replayCachedRequests(): void {
    if (this.isOffline) return;

    const cachedRequests = JSON.parse(localStorage.getItem('cachedRequests') || '[]');
    cachedRequests.forEach((request: any) => {
      const { method, endPoint, body } = request;
      if (method === 'POST') {
        this.http.post(`${this.baseURL}${endPoint}`, body).subscribe();
      }
      // Extend for other HTTP methods as needed
    });

    localStorage.removeItem('cachedRequests');
  }


  // === Image Requests ===
  async getImage(imageName: string): Promise<string | null> {
    if (!imageName || typeof imageName !== 'string' || imageName.trim() === '') {
      return null;
    }

    if (imageName.startsWith('data:') || imageName.includes('///')) {
      return imageName;
    }

    const fileName = imageName.split('/').pop();
    const key = `/${fileName}`;
    const cachedImage = await this.cacher.get(key);

    if (cachedImage) {
      return cachedImage;
    }

    if (this.isOffline) {
      this.helpers.presentToast('انت غير متصل بالانترنت', true);
      console.warn('App is offline. Cannot fetch image.');
      return null;
    }

    try {
      const response = await this.http
        .get(`${environment.source}/${key}`, { responseType: 'blob' })
        .toPromise();
      const base64 = await this.convertImageToBase64(response);
      await this.cacher.set(key, base64);
      return `data:${this.inferMimeType(imageName)};base64,${base64}`;
    } catch (error) {
      console.error(`Error fetching image ${imageName}:`, error);
      return null;
    }
  }

  private inferMimeType(fileName: string): string | null {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'svg':
        return 'image/svg+xml';
      default:
        return null;
    }
  }

  private convertImageToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]); // Only return Base64 data
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  get refresh(): Observable<string> {
    return this.refreshBehaviorSubject.asObservable();
  }

  set refresh(data: string) {
    this.refreshBehaviorSubject.next(data);
  }

  get api(): string {
    return this.baseURL;
  }
}
